import { Controller } from "@hotwired/stimulus"

// Таблица получателей: строки имя/номер/размер, импорт .xlsx/.csv, AI-распознавание
// вставленного текста, сбор строк → PATCH save. Размеры берём из data-sizes (размеры заказа).
//
// Скрытое поле `id` в строке — не косметика: сервер по нему обновляет существующего
// получателя вместо пересоздания, а на recipient_id висят персональные правки макета.
export default class extends Controller {
  static targets = ["rows", "text", "status", "issues", "empty"]
  static values = { saveUrl: String, parseUrl: String, importUrl: String, sizes: Array }

  addRow() { this.insertRow({ name: "", number: "", size: this.sizesValue[0] || "" }) }

  esc(s) { const d = document.createElement("div"); d.textContent = s ?? ""; return d.innerHTML }

  knownSize(size) { return this.sizesValue.includes(size) }

  insertRow(r) {
    const unknown = !this.knownSize(r.size)
    const tr = document.createElement("tr")
    tr.className = unknown ? "border-b border-[#f2f5f8] bg-[#fdf1f2]" : "border-b border-[#f2f5f8]"
    // незнакомый размер добавляем опцией, иначе select молча подменит его первым из списка
    const opts = (unknown && r.size ? [r.size, ...this.sizesValue] : this.sizesValue)
      .map((s) => `<option ${s === r.size ? "selected" : ""}>${this.esc(s)}</option>`).join("")
    const inputCls = "h-[38px] w-full rounded-[8px] border px-[11px] text-sm text-[#1b1f2a]"
    tr.innerHTML = `
      <input type="hidden" data-f="id" value="${this.esc(r.id ?? "")}"/>
      <td class="px-[14px] py-[9px]"><input data-f="name" value="${this.esc(r.name)}" class="${inputCls} border-[#dce3ea]"/></td>
      <td class="px-[14px] py-[9px]"><input data-f="number" value="${this.esc(r.number)}" class="${inputCls} border-[#dce3ea]"/></td>
      <td class="px-[14px] py-[9px]">
        <select data-f="size" data-action="recipients#sizeChanged"
                class="${inputCls} cursor-pointer bg-white ${unknown ? "border-[#c33245]" : "border-[#dce3ea]"}"
                ${unknown ? 'title="Такого размера нет в заказе"' : ""}>${opts}</select>
      </td>
      <td class="py-[9px] pl-[6px] pr-[14px]">
        <button type="button" data-action="recipients#removeRow" title="Удалить строку"
                class="flex h-[38px] w-[38px] items-center justify-center rounded-[8px] border border-[#f0f3f7] bg-white text-[#c33245] hover:bg-[#fdf1f2]">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6 6 18"/></svg>
        </button>
      </td>`
    this.rowsTarget.appendChild(tr)
    this.hideEmptyNote()
  }

  removeRow(e) { e.target.closest("tr").remove() }

  // размер поправили руками → снять подсветку «нет в заказе»
  sizeChanged(e) {
    const tr = e.target.closest("tr")
    const ok = this.knownSize(e.target.value)
    tr.className = ok ? "border-b border-[#f2f5f8]" : "border-b border-[#f2f5f8] bg-[#fdf1f2]"
    e.target.classList.toggle("border-[#c33245]", !ok)
    e.target.classList.toggle("border-[#dce3ea]", ok)
  }

  hideEmptyNote() { if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden") }

  collect() {
    return [...this.rowsTarget.querySelectorAll("tr")].map((tr, i) => ({
      id: tr.querySelector('[data-f="id"]')?.value || null,
      name: tr.querySelector('[data-f="name"]').value,
      number: tr.querySelector('[data-f="number"]').value,
      size: tr.querySelector('[data-f="size"]').value,
      position: i,
    }))
  }

  replaceRows(rows) {
    this.rowsTarget.innerHTML = ""
    rows.forEach((r) => this.insertRow(r))
    this.hideEmptyNote()
  }

  async save() {
    const rows = this.collect()
    const bad = rows.filter((r) => !this.knownSize(r.size))
    if (bad.length) {
      this.showIssues([`Не сохранили: у ${bad.length} строк размер отсутствует в заказе. Исправьте размер или добавьте его в состав заказа.`], [])
      this.statusTarget.textContent = "Есть незнакомые размеры"
      return
    }
    const res = await fetch(this.saveUrlValue, {
      method: "PATCH", headers: this.headers(), body: JSON.stringify({ recipients: rows }),
    })
    if (!res.ok) { this.statusTarget.textContent = "Ошибка сохранения"; return }
    // перерисовываем из ответа — у новых строк появляются id, без них следующее
    // сохранение пересоздало бы получателей
    const data = await res.json().catch(() => null)
    if (data?.recipients) this.replaceRows(data.recipients)
    this.showIssues([], [])
    this.statusTarget.textContent = "Сохранено"
    // ростер изменился → пересчитать «у кого не влезло» (карточка ниже слушает событие)
    window.dispatchEvent(new CustomEvent("roster:changed"))
  }

  async importFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ""   // чтобы повторный выбор того же файла снова сработал
    this.statusTarget.textContent = `Читаю ${file.name}…`
    const body = new FormData()
    body.append("file", file)
    const res = await fetch(this.importUrlValue, {
      method: "POST", body,
      headers: { "Accept": "application/json", "X-CSRF-Token": this.csrf() },
    })
    const data = await res.json().catch(() => null)
    if (!res.ok || !data) {
      this.showIssues(data?.errors || ["Не удалось прочитать файл."], data?.warnings || [])
      this.statusTarget.textContent = "Импорт не удался"
      return
    }
    this.replaceRows(data.rows)
    this.showIssues([], data.warnings || [])
    this.statusTarget.textContent = `Загружено строк: ${data.rows.length}. Проверьте и сохраните.`
  }

  showIssues(errors, warnings) {
    if (!this.hasIssuesTarget) return
    const item = (text, err) =>
      `<li class="${err ? "text-[#c33245]" : "text-[#8a6d20]"}">${this.esc(text)}</li>`
    const items = [...errors.map((t) => item(t, true)), ...warnings.map((t) => item(t, false))].join("")
    this.issuesTarget.innerHTML = items
      ? `<ul class="mt-3 space-y-1 rounded-[10px] bg-[#fbf7ec] px-[13px] py-[10px] text-[13px] leading-[1.45]">${items}</ul>`
      : ""
  }

  async recognize() {
    this.statusTarget.textContent = "Распознаю…"
    const res = await fetch(this.parseUrlValue, {
      method: "POST", headers: this.headers(),
      body: JSON.stringify({ text: this.textTarget.value }),
    })
    const data = await res.json().catch(() => ({ rows: null }))
    if (!data.rows) { this.statusTarget.textContent = "Не удалось распознать — введите вручную"; return }
    this.replaceRows(data.rows)
    this.statusTarget.textContent = `Распознано строк: ${data.rows.length}`
  }

  csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }

  headers() {
    return {
      "Content-Type": "application/json",
      // Accept JSON — иначе respond_to выберет html-ветку (redirect 302), и fetch
      // повторит PATCH с JSON-телом на /orders/1 → 400 «ошибка сохранения».
      "Accept": "application/json",
      "X-CSRF-Token": this.csrf(),
    }
  }
}
